/**
 * Cumple — Mapping de plantilla → categoría/familia (Sprint 12).
 *
 * Las 4 familias coinciden con las que se muestran en la landing
 * (apps/web/src/pages/Pricing.tsx → TEMPLATE_FAMILIES) — coherencia
 * comercial ↔ producto.
 *
 * Sprint Y — Se reescribió CATEGORY_RULES de prefijo-string a regex
 * case-insensitive para cubrir los guiones del catálogo real (el código
 * original usaba underscores, el catálogo usa guiones). Meta: ≤10% en
 * `other`. Ver ADR-019 nota 2026-05-28.
 */

/**
 * Array canónico de categorías válidas. Exportado como `as const` para
 * que TypeScript infiera el tipo literal de cada elemento.
 *
 * Es la fuente única de verdad para:
 *   - el type TemplateCategory (derivado)
 *   - el CHECK constraint survey_templates_category_check (verificado en CI)
 *   - los tests de paridad en apps/api/src/templates/category-parity.test.ts
 *
 * Si se añade una categoría aquí SIN crear la migración correspondiente,
 * el test de paridad CI falla.
 */
export const TEMPLATE_CATEGORIES = [
  'physical_security',
  'cybersecurity',
  'regulatory_rd',
  'customs_food',
  'other',
] as const;

export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

/**
 * Reglas de categorización por regex sobre el `code` completo.
 * El orden importa — se usa la PRIMERA que coincide (early-exit).
 *
 * Razón del cambio a regex: el catálogo usa guiones (ej. `nist-csf-cyber-*`)
 * mientras que el código original usaba underscores (ej. `nist_csf`).
 * El regex `([-_]|$)` acepta ambos separadores + ausencia de sufijo.
 */
const CATEGORY_RULES: { match: RegExp; category: TemplateCategory }[] = [
  // ── Seguridad física ──────────────────────────────────────────────────
  { match: /^(cctv|acs|intrusion|fire|tvra)([-_]|$)/i, category: 'physical_security' },

  // ── Regulación sectorial RD ───────────────────────────────────────────
  // Va ANTES del comodín -cyber- porque varios templates regulatorios RD
  // incluyen "-cyber-" en su código (ej. jm-rd-cyber-auditor-v1,
  // indotel-rd-cyber-auditor-v1, simv-rd-cyber-*).
  {
    match:
      /^(jm[-_]rd|indotel[-_]rd|simv|nortic[-_]?a\d?|sbr[-_]rd|ley\d+[-_]rd|hacienda[-_]rd|gob[-_]corp[-_]rd|gir[-_]rd|conc[-_]riesgos|cambiario[-_]rd|proteccion[-_]usuario)([-_]|$)/i,
    category: 'regulatory_rd',
  },

  // ── Aduanas, inocuidad y gestión de calidad/HSEQ ─────────────────────
  // Va antes del comodín -cyber- para que cnzfe/oea/bpm/haccp y los HSEQ
  // no caigan en cybersecurity por algún sufijo accidental.
  {
    match:
      /^(oea[-_]rd|cnzfe|bpm|haccp|iso22000|iso13485|iso14001|iso45001|iso50001|iso9001)([-_]|$)/i,
    category: 'customs_food',
  },

  // ── Cybersecurity ─────────────────────────────────────────────────────
  // Estándares ISO de seguridad de la información y continuidad
  {
    match: /^(iso2700[157]|iso27701|iso22301)([-_]|$)/i,
    category: 'cybersecurity',
  },
  // NIST CSF (ambas formas: nist-csf y nist_csf)
  { match: /^nist[-_]?csf([-_]|$)/i, category: 'cybersecurity' },
  // CIS Controls
  { match: /^cis([-_]|$)/i, category: 'cybersecurity' },
  // COBIT, ITIL, ISO 20000 (marcos ITSM que MSSPRD audita bajo cyber)
  { match: /^(cobit|itil|iso20000)([-_]|$)/i, category: 'cybersecurity' },
  // Cloud, zero-trust, ransomware, it-risk, GDPR, PCI-DSS
  {
    match: /^(cloud[-_]security|zero[-_]trust|ransomware|it[-_]risk|gdpr|pci[-_]dss)([-_]|$)/i,
    category: 'cybersecurity',
  },
  // Cualquier template con `-cyber-` en el código (ej. ai-governance-maturity-cyber-auditor-v1,
  // cis-cyber-auditor-v1, iso27001-cyber-auditor-v1, etc.)
  { match: /-cyber-/i, category: 'cybersecurity' },
  // Gobernanza / GRC corporativo / IA — provisional bajo cyber (Sprint Y)
  // TODO(sprint-z): si el PM decide separar ISO 31000/37001/37301/42001 en
  // categoría propia, se necesita migración CHECK + ADR nuevo.
  {
    match: /^(iso31000|iso37001|iso37301|iso42001|ai[-_]governance)([-_]|$)/i,
    category: 'cybersecurity',
  },
];

export function categoryForCode(code: string): TemplateCategory {
  for (const rule of CATEGORY_RULES) {
    if (rule.match.test(code)) return rule.category;
  }
  return 'other';
}

export interface CategoryMeta {
  category: TemplateCategory;
  label: string;
  short: string;
  description: string;
  /** Tailwind utility classes para badges/cards. Hex aproximado en comentario. */
  toneClasses: { bg: string; text: string; ring: string };
  order: number;
}

export const CATEGORY_META: Record<TemplateCategory, CategoryMeta> = {
  physical_security: {
    category: 'physical_security',
    label: 'Seguridad física',
    short: 'Físico',
    description: 'CCTV, control de acceso, intrusión, incendio, TVRA',
    toneClasses: { bg: 'bg-blue-50', text: 'text-brand-700', ring: 'ring-blue-200' },
    order: 1,
  },
  cybersecurity: {
    category: 'cybersecurity',
    label: 'Cybersecurity',
    short: 'Cyber',
    description: 'ISO 27001, NIST CSF, CIS, ISO 27005, ISO 22301',
    toneClasses: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
    order: 2,
  },
  regulatory_rd: {
    category: 'regulatory_rd',
    label: 'Regulación sectorial RD',
    short: 'Reg. RD',
    description: 'JM SB, INDOTEL, SIMV, NORTIC A2/A3/A4/A7/A8',
    toneClasses: { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' },
    order: 3,
  },
  customs_food: {
    category: 'customs_food',
    label: 'Aduanas e inocuidad',
    short: 'Aduanas',
    description: 'OEA RD, Zona Franca, BPM, HACCP',
    toneClasses: { bg: 'bg-purple-50', text: 'text-purple-700', ring: 'ring-purple-200' },
    order: 4,
  },
  other: {
    category: 'other',
    label: 'Otras',
    short: 'Otra',
    description: 'Plantillas sin clasificar',
    toneClasses: { bg: 'bg-slate-100', text: 'text-slate-700', ring: 'ring-slate-200' },
    order: 99,
  },
};

export function categoryLabelForCode(code: string): string {
  return CATEGORY_META[categoryForCode(code)].label;
}

/**
 * Agrupa un listado de elementos por categoría (basado en code). Devuelve
 * un array ordenado según `CATEGORY_META.order`. Conveniencia para UIs
 * que renderean grupos con header.
 */
export interface CategoryGroup<T> {
  category: TemplateCategory;
  meta: CategoryMeta;
  items: T[];
}

export function groupByCategory<T>(items: T[], getCode: (item: T) => string): CategoryGroup<T>[] {
  const bucket = new Map<TemplateCategory, T[]>();
  for (const item of items) {
    const cat = categoryForCode(getCode(item));
    if (!bucket.has(cat)) bucket.set(cat, []);
    bucket.get(cat)!.push(item);
  }
  return [...bucket.entries()]
    .map(([category, items]) => ({ category, meta: CATEGORY_META[category], items }))
    .sort((a, b) => a.meta.order - b.meta.order);
}
